import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, Container, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, LinearProgress, MenuItem, Select, Stack,
  Step, StepButton, StepLabel, Stepper, TextField, Typography,
} from '@mui/material';
import { useProject } from '../../hooks/ProjectContext';
import Iconify from '../../components/iconify';
import MiniMapPreview from '../../components/map/MiniMapPreview';

// ---------------------------------------------------------------------------
const WORKFLOW_STEPS = [
  { key: 'layers',  label: 'Layers' },
  { key: 'strata',  label: 'Strata' },
  { key: 'samples', label: 'Samples' },
];

const LAYER_OPTIONS = [
  { key: 'NDVI',     label: 'NDVI',                  temporal: true },
  { key: 'MNDWI',   label: 'Water Index',            temporal: true },
  { key: 'BARESOIL',label: 'Bare Soil',              temporal: true },
  { key: 'S1VV',    label: 'Moisture (Radar)',       temporal: true },
  { key: 'THERMAL', label: 'Temperature',            temporal: true },
  { key: 'ELEVATION',label: 'Elevation' },
  { key: 'SLOPE',   label: 'Slope' },
  { key: 'ASPECT',  label: 'Aspect' },
  { key: 'TPI',     label: 'Topographic Position' },
  { key: 'TWI',     label: 'Wetness Index' },
];

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ---------------------------------------------------------------------------
export default function StratificationPage() {
  const { selectedProject, selectedSite } = useProject();

  const [activeStep, setActiveStep] = useState('layers');
  const [geeReady, setGeeReady] = useState(false);
  const geeRef = useCallback(node => {
    if (!node) return;
    // Listen for gee_ready from iframe
    const handler = (e) => {
      if (e.data?.type === 'gee_ready') setGeeReady(true);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Send ROI to GEE iframe when ready
  const sendRoiToGee = useCallback((roiData) => {
    const iframe = document.getElementById('gee-layers-iframe');
    if (iframe?.contentWindow && roiData) {
      iframe.contentWindow.postMessage({ type: 'set_roi', payload: roiData }, '*');
    }
  }, []);

  // ROI (needed for export/clustering)
  const [roiGeometry, setRoiGeometry] = useState(null);
  const [bbox, setBbox] = useState(null);
  const [kmlName, setKmlName] = useState('');
  const [kmlError, setKmlError] = useState('');

  // Layers step
  const [viewLayers, setViewLayers] = useState(['S1VV', 'TWI', 'NDVI', 'BARESOIL', 'ELEVATION']);
  const [dateStart, setDateStart] = useState('2023-01-01');
  const [dateEnd, setDateEnd] = useState('2026-01-01');
  const [rasterTitle, setRasterTitle] = useState('');

  // Strata step
  const [selectedRasters, setSelectedRasters] = useState([]);
  const [clusters, setClusters] = useState(13);
  const [trainPixels, setTrainPixels] = useState(8000);
  const [strataTitle, setStrataTitle] = useState('');
  const [useSmoothing, setUseSmoothing] = useState(true);
  const [smoothRadius, setSmoothRadius] = useState(3);

  // Samples step
  const [selectedStrata, setSelectedStrata] = useState('');
  const [pointsPerStratum, setPointsPerStratum] = useState(4);
  const [minDistance, setMinDistance] = useState(50);
  const [samplingTitle, setSamplingTitle] = useState('');

  // Artifacts + status
  const [artifacts, setArtifacts] = useState([]);
  const [status, setStatus] = useState('');
  const [running, setRunning] = useState(false);

  // Wizard
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(null);
  const [wizardResults, setWizardResults] = useState({});
  const [wizardParams, setWizardParams] = useState({
    layers: ['S1VV', 'TWI', 'NDVI', 'BARESOIL', 'ELEVATION'], clusters: 13, train: 8000, seed: 42,
    points: 4, distance: 50, dateStart: '2023-01-01', dateEnd: '2026-01-01',
    useSmoothing: true, smoothRadius: 3,
  });

  // --- Fetch ROI
  useEffect(() => {
    if (!selectedProject?.code) { setRoiGeometry(null); return; }
    const params = new URLSearchParams({ project: selectedProject.code });
    if (selectedSite?.code) params.set('site', selectedSite.code);
    fetch(`/v1/stratification/geometry?${params}`)
      .then(r => r.ok ? r.json() : null)
      .then(fc => setRoiGeometry(fc || null))
      .catch(() => setRoiGeometry(null));
  }, [selectedProject, selectedSite]);

  // Map preview data (per-step: strata shows selected strata, samples shows strata+points)
  const [mapStrata, setMapStrata] = useState(null);
  const [mapPoints, setMapPoints] = useState([]);
  const [previewStrataId, setPreviewStrataId] = useState(null);
  const [previewPointsId, setPreviewPointsId] = useState(null);

  // --- Fetch artifacts
  const loadArtifacts = useCallback(() => {
    if (!selectedProject?.code) { setArtifacts([]); return; }
    fetch(`/v1/stratification/artifacts?project=${selectedProject.code}`)
      .then(r => r.ok ? r.json() : [])
      .then(rows => setArtifacts(Array.isArray(rows) ? rows : []))
      .catch(() => setArtifacts([]));
  }, [selectedProject]);
  useEffect(() => { loadArtifacts(); }, [loadArtifacts]);

  // Send ROI to GEE iframe when boundary loads
  useEffect(() => {
    if (geeReady && roiGeometry) sendRoiToGee(roiGeometry);
  }, [geeReady, roiGeometry, sendRoiToGee]);

  // Load strata + points for map preview based on step and selection
  useEffect(() => {
    const sorted = [...artifacts].sort((a, b) => b.id - a.id);

    // For strata step: use previewStrataId or latest strata
    const strataId = previewStrataId || sorted.find(a => a.subtype === 'strata')?.id;
    if (strataId) {
      fetch(`/v1/stratification/artifacts/${strataId}`).then(r => r.ok ? r.json() : null)
        .then(d => {
          if (!d) return;
          const features = MiniMapPreview.parseFeatures(d);
          if (features.length) setMapStrata({ type: 'FeatureCollection', features });
          else setMapStrata(null);
        }).catch(() => setMapStrata(null));
    } else { setMapStrata(null); }

    // For samples step: use previewPointsId or latest points
    const pointsId = previewPointsId || sorted.find(a => a.subtype === 'points')?.id;
    if (pointsId) {
      fetch(`/v1/stratification/artifacts/${pointsId}`).then(r => r.ok ? r.json() : null)
        .then(d => {
          if (!d) return;
          const features = MiniMapPreview.parseFeatures(d).filter(f => f.geometry?.type === 'Point');
          setMapPoints(features.length ? features : []);
        }).catch(() => setMapPoints([]));
    } else { setMapPoints([]); }
  }, [artifacts, previewStrataId, previewPointsId]);

  // --- KML upload (bbox for ROI override)
  function handleKmlFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setKmlError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const doc = new DOMParser().parseFromString(ev.target.result, 'application/xml');
        const coordEls = Array.from(doc.getElementsByTagName('coordinates'));
        let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity, count = 0;
        for (const el of coordEls) {
          for (const pair of el.textContent.trim().split(/\s+/)) {
            const [lng, lat] = pair.split(',').map(Number);
            if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
            if (lng < minLng) minLng = lng; if (lng > maxLng) maxLng = lng;
            if (lat < minLat) minLat = lat; if (lat > maxLat) maxLat = lat;
            count++;
          }
        }
        if (count === 0) throw new Error('No coordinates found');
        setBbox({ minLng, minLat, maxLng, maxLat });
        setKmlName(file.name);
      } catch (err) { setKmlError(err.message); }
    };
    reader.readAsText(file);
  }

  // Effective ROI for API calls (bbox overrides geometry)
  const effectiveRoi = bbox
    ? { type: 'Feature', geometry: { type: 'Polygon', coordinates: [[[bbox.minLng, bbox.minLat],[bbox.maxLng, bbox.minLat],[bbox.maxLng, bbox.maxLat],[bbox.minLng, bbox.maxLat],[bbox.minLng, bbox.minLat]]] } }
    : roiGeometry;

  // ---------------------------------------------------------------------------
  // Step actions
  // ---------------------------------------------------------------------------

  async function handleExportRaster() {
    if (!effectiveRoi || viewLayers.length === 0) return;
    setRunning(true);
    setStatus('Exporting raster to GEE Asset…');
    try {
      const res = await fetch('/v1/ee/export-raster', {
        method: 'POST',
                              headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('tkn') ? { Authorization: `Bearer ${localStorage.getItem('tkn')}` } : {}) },
        body: JSON.stringify({
          projectId: selectedProject?.code, siteId: selectedSite?.code,
          layers: viewLayers, dateStart, dateEnd, roi: effectiveRoi, scale: 30,
          title: rasterTitle || undefined,
        }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.message);
      setStatus('Export started — polling…');
      pollArtifact(json.id, () => {
        setStatus('Raster exported. Select it in the Strata step.');
        setRunning(false);
        loadArtifacts();
      });
    } catch (e) {
      setStatus('Export failed: ' + e.message);
      setRunning(false);
    }
  }

  async function handleRunClustering() {
    if (selectedRasters.length === 0) return;
    setRunning(true);
    setStatus('Running clustering…');
    try {
      const assetIds = [];
      for (const rid of selectedRasters) {
        const art = artifacts.find(a => String(a.id) === rid);
        const p = art ? (typeof art.params_json === 'string' ? JSON.parse(art.params_json) : art.params_json) : {};
        if (p.assetId) assetIds.push(p.assetId);
      }
      if (assetIds.length === 0) throw new Error('Selected rasters have no GEE assets. Export them first in the Layers step.');

      const res = await fetch('/v1/ee/run-clustering', {
        method: 'POST',
                              headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('tkn') ? { Authorization: `Bearer ${localStorage.getItem('tkn')}` } : {}) },
        body: JSON.stringify({ assetIds, roi: effectiveRoi, nClusters: clusters, trainPixels, seed: 42, smoothRadius: useSmoothing ? smoothRadius : 0 }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.message);

      await fetch('/v1/stratification/artifacts', {
        method: 'POST',
                              headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('tkn') ? { Authorization: `Bearer ${localStorage.getItem('tkn')}` } : {}) },
        body: JSON.stringify({
          projectId: selectedProject?.code, siteId: selectedSite?.code || '',
          tool: 'stratification', subtype: 'strata',
          title: strataTitle || `${clusters} clusters — ${new Date().toLocaleDateString()}`,
          params: { nClusters: clusters, assetIds },
          payload: json.strata,
        }),
      });

      setStatus(`Clustering done: ${json.strata.features.length} polygons saved. View in Map (globe icon).`);
      setRunning(false);
      loadArtifacts();
    } catch (e) {
      setStatus('Clustering failed: ' + e.message);
      setRunning(false);
    }
  }

  async function handleGeneratePoints() {
    if (!selectedStrata) return;
    setRunning(true);
    setStatus('Generating sample points…');
    try {
      const art = artifacts.find(a => String(a.id) === selectedStrata);
      if (!art) throw new Error('Strata artifact not found');
      const full = await fetch(`/v1/stratification/artifacts/${art.id}`).then(r => r.json());
      const strataPayload = typeof full.payload_json === 'string' ? JSON.parse(full.payload_json) : full.payload_json;
      if (!strataPayload?.features) throw new Error('No strata polygons');

      const res = await fetch('/v1/ee/generate-points', {
        method: 'POST',
                              headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('tkn') ? { Authorization: `Bearer ${localStorage.getItem('tkn')}` } : {}) },
        body: JSON.stringify({ strata: strataPayload, pointsPerStratum, minDistance }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.message);

      const merged = { type: 'FeatureCollection', features: [...strataPayload.features, ...json.points.features] };
      await fetch('/v1/stratification/artifacts', {
        method: 'POST',
                              headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('tkn') ? { Authorization: `Bearer ${localStorage.getItem('tkn')}` } : {}) },
        body: JSON.stringify({
          projectId: selectedProject?.code, siteId: selectedSite?.code || '',
          tool: 'sampling-points', subtype: 'points',
          title: samplingTitle || `${json.points.features.length} points — ${new Date().toLocaleDateString()}`,
          params: { pointsPerStratum, minDistance, numPoints: json.points.features.length },
          payload: merged,
        }),
      });

      setStatus(`${json.points.features.length} sample points generated and saved. View in Map (globe icon).`);
      setRunning(false);
      loadArtifacts();
    } catch (e) {
      setStatus('Generation failed: ' + e.message);
      setRunning(false);
    }
  }

  function pollArtifact(id, onDone) {
    const poll = setInterval(async () => {
      try {
        const art = await fetch(`/v1/stratification/artifacts/${id}`).then(r => r.json());
        const p = typeof art.params_json === 'string' ? JSON.parse(art.params_json) : (art.params_json || {});
        if (p.status === 'ready') { clearInterval(poll); onDone(); }
        else if (p.status === 'failed') { clearInterval(poll); setStatus('Export failed: ' + (p.error || '')); setRunning(false); }
      } catch { /* keep polling */ }
    }, 5000);
  }

  // ---------------------------------------------------------------------------
  // Wizard
  // ---------------------------------------------------------------------------
  async function runWizardStep(step, prevResult) {
    setWizardStep(step);
    const p = wizardParams;
    const projectId = selectedProject?.code || '';
    const siteId = selectedSite?.code || '';
    const ts = new Date().toISOString().replace(/[:.T]/g, '-').slice(0, 19);
    try {
      if (step === 'layers') {
        const res = await fetch('/v1/ee/export-raster', {
          method: 'POST',
                              headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('tkn') ? { Authorization: `Bearer ${localStorage.getItem('tkn')}` } : {}) },
          body: JSON.stringify({ projectId, siteId, layers: p.layers, dateStart: p.dateStart, dateEnd: p.dateEnd, roi: effectiveRoi, scale: 30, title: `auto-layers-${ts}` }),
        });
        const json = await res.json();
        if (!json.ok) throw new Error(json.message);
        setWizardResults(prev => ({ ...prev, layersId: json.id, assetId: json.assetId }));
        await new Promise((resolve, reject) => {
          const poll = setInterval(async () => {
            try {
              const r = await fetch(`/v1/stratification/artifacts/${json.id}`).then(r => r.json());
              const pp = typeof r.params_json === 'string' ? JSON.parse(r.params_json) : r.params_json;
              if (pp.status === 'ready') { clearInterval(poll); resolve(); }
              else if (pp.status === 'failed') { clearInterval(poll); reject(new Error(pp.error || 'Export failed')); }
            } catch { /* keep polling */ }
          }, 5000);
        });
        setWizardStep('layers-done');
        setWizardResults(prev => ({ ...prev, layers: true }));
        loadArtifacts();
        return { assetId: json.assetId };

      } else if (step === 'strata') {
        const assetId = prevResult?.assetId || wizardResults.assetId;
        if (!assetId) throw new Error('No raster — run Step 1 first');
        const res = await fetch('/v1/ee/run-clustering', {
          method: 'POST',
                              headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('tkn') ? { Authorization: `Bearer ${localStorage.getItem('tkn')}` } : {}) },
          body: JSON.stringify({ assetId, roi: effectiveRoi, nClusters: p.clusters, trainPixels: p.train, seed: p.seed, smoothRadius: p.useSmoothing ? p.smoothRadius : 0 }),
        });
        const json = await res.json();
        if (!json.ok) throw new Error(json.message);
        await fetch('/v1/stratification/artifacts', {
          method: 'POST',
                              headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('tkn') ? { Authorization: `Bearer ${localStorage.getItem('tkn')}` } : {}) },
          body: JSON.stringify({ projectId, siteId, tool: 'stratification', subtype: 'strata',
            title: `auto-strata-${ts}`, params: { nClusters: p.clusters, assetId }, payload: json.strata }),
        });
        setWizardResults(prev => ({ ...prev, strata: true, strataPayload: json.strata }));
        setWizardStep('strata-done');
        loadArtifacts();
        return { strataPayload: json.strata };

      } else if (step === 'sampling') {
        const strata = prevResult?.strataPayload || wizardResults.strataPayload;
        if (!strata) throw new Error('No strata — run Step 2 first');
        const res = await fetch('/v1/ee/generate-points', {
          method: 'POST',
                              headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('tkn') ? { Authorization: `Bearer ${localStorage.getItem('tkn')}` } : {}) },
          body: JSON.stringify({ strata, pointsPerStratum: p.points, minDistance: p.distance }),
        });
        const json = await res.json();
        if (!json.ok) throw new Error(json.message);
        const merged = { type: 'FeatureCollection', features: [...strata.features, ...json.points.features] };
        await fetch('/v1/stratification/artifacts', {
          method: 'POST',
                              headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('tkn') ? { Authorization: `Bearer ${localStorage.getItem('tkn')}` } : {}) },
          body: JSON.stringify({ projectId, siteId, tool: 'sampling-points', subtype: 'points',
            title: `auto-sampling-${ts}`, params: { pointsPerStratum: p.points, minDistance: p.distance }, payload: merged }),
        });
        setWizardResults(prev => ({ ...prev, sampling: true }));
        setWizardStep('done');
        loadArtifacts();
      }
    } catch (e) {
      setWizardStep(step + '-error');
      setWizardResults(prev => ({ ...prev, error: e.message }));
      throw e;
    }
  }

  async function runAllWizardSteps() {
    try {
      const r1 = await runWizardStep('layers');
      const r2 = await runWizardStep('strata', r1);
      await runWizardStep('sampling', r2);
    } catch { /* error shown in UI */ }
  }

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------
  const rasterArtifacts = artifacts.filter(a => a.tool === 'time-explorer' && (a.subtype === 'stack' || a.subtype === 'upload'));
  const strataArtifacts = artifacts.filter(a => a.subtype === 'strata' || (a.tool === 'stratification' && a.subtype === 'upload'));
  const pointsArtifacts = artifacts.filter(a => a.tool === 'sampling-points');
  const stepCompletion = {
    layers: rasterArtifacts.length > 0,
    strata: strataArtifacts.length > 0,
    samples: artifacts.some(a => a.tool === 'sampling-points'),
  };

  // =========================================================================
  return (
    <>
      <Helmet><title>Stratification | TERRRA</title></Helmet>
      <Container maxWidth="xl" sx={{ py: 1 }}>

        {/* Stepper */}
        <Stepper nonLinear activeStep={WORKFLOW_STEPS.findIndex(s => s.key === activeStep)} sx={{ mb: 1 }}>
          {WORKFLOW_STEPS.map((step) => (
            <Step key={step.key} completed={stepCompletion[step.key]}>
              <StepButton onClick={() => setActiveStep(step.key)}>
                <StepLabel>
                  <Typography sx={{
                    fontWeight: activeStep === step.key ? 'bold' : 'normal',
                    fontSize: activeStep === step.key ? '1.1rem' : '0.875rem',
                    textDecoration: activeStep === step.key ? 'underline' : 'none',
                    color: activeStep === step.key ? 'primary.main' : 'text.secondary',
                  }}>
                    {step.label}
                  </Typography>
                </StepLabel>
              </StepButton>
            </Step>
          ))}
        </Stepper>

        {/* Top action bar */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
          {bbox && <Chip label={`KML ROI: ${kmlName}`} size="small" color="success" onDelete={() => { setBbox(null); setKmlName(''); }} />}
          {status && <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{status}</Typography>}
          {!status && <Box sx={{ flex: 1 }} />}
          <Button size="small" variant="outlined" component="label">
            Upload KML ROI <input hidden type="file" accept=".kml" onChange={handleKmlFile} />
          </Button>
          <Button size="small" variant="contained" color="warning" startIcon={<Iconify icon="eva:flash-outline" />}
            onClick={() => setWizardOpen(true)} disabled={running || !selectedProject}>
            Auto
          </Button>
        </Stack>

        {kmlError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setKmlError('')}>{kmlError}</Alert>}
        {!roiGeometry && !bbox && selectedProject && (
          <Alert severity="warning" sx={{ mb: 1 }}>No boundary found for this project. Upload a KML ROI to proceed.</Alert>
        )}

        {running && <LinearProgress sx={{ mb: 1 }} />}

        {/* Step cards */}
        <Card variant="outlined">
          <CardContent>

            {/* ---- LAYERS ---- */}
            {activeStep === 'layers' && (
              <Stack spacing={2}>
                <Typography variant="h6">Select Layers to Export</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                  {LAYER_OPTIONS.map(l => (
                    <FormControlLabel key={l.key} sx={{ width: '20%', minWidth: 140, m: 0 }}
                      label={<Typography variant="caption">{l.label}</Typography>}
                      control={<Checkbox size="small" checked={viewLayers.includes(l.key)}
                        onChange={e => setViewLayers(prev => e.target.checked ? [...prev, l.key] : prev.filter(x => x !== l.key))} />}
                    />
                  ))}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <TextField size="small" label="Start" value={dateStart} onChange={e => setDateStart(e.target.value)} sx={{ width: 130 }} />
                  <TextField size="small" label="End" value={dateEnd} onChange={e => setDateEnd(e.target.value)} sx={{ width: 130 }} />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField size="small" label="Raster name (optional)" value={rasterTitle}
                    onChange={e => setRasterTitle(e.target.value)}
                    placeholder={viewLayers.join(',') + ' — ' + new Date().toLocaleDateString()}
                    sx={{ flex: 1 }} />
                  <Button variant="contained" onClick={handleExportRaster}
                    disabled={running || viewLayers.length === 0 || (!effectiveRoi)}>
                    {running ? 'Exporting…' : 'Export Raster'}
                  </Button>
                </Stack>

                {rasterArtifacts.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                      Exported Rasters ({rasterArtifacts.length})
                    </Typography>
                    {rasterArtifacts.map(a => {
                      const p = typeof a.params_json === 'string' ? JSON.parse(a.params_json) : (a.params_json || {});
                      return (
                        <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                          <Chip size="small" label={p.status === 'ready' ? 'ready' : p.status || 'pending'}
                            color={p.status === 'ready' ? 'success' : 'default'} />
                          <Typography variant="caption">{a.title || a.subtype} — {fmtDate(a.created_at)}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Stack>
            )}

            {/* ---- STRATA ---- */}
            {activeStep === 'strata' && (
              <Stack spacing={2}>
                <Typography variant="h6">Run Clustering Analysis</Typography>
                <Typography variant="body2" color="text.secondary">
                  Select one or more exported rasters — bands will be merged into a single classification.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                  {rasterArtifacts.map(a => {
                    const p = typeof a.params_json === 'string' ? JSON.parse(a.params_json) : (a.params_json || {});
                    const id = String(a.id);
                    return (
                      <FormControlLabel key={a.id} sx={{ width: '50%', m: 0 }}
                        label={<Typography variant="caption">{a.title || p.layers || 'Raster'} — {fmtDate(a.created_at)}{!p.assetId && ' (no asset)'}</Typography>}
                        control={<Checkbox size="small" checked={selectedRasters.includes(id)}
                          onChange={e => setSelectedRasters(prev => e.target.checked ? [...prev, id] : prev.filter(x => x !== id))} />}
                      />
                    );
                  })}
                  {rasterArtifacts.length === 0 && (
                    <Typography variant="caption" color="text.secondary">No rasters yet — export one in the Layers step.</Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2">Clusters:</Typography>
                  <TextField size="small" type="number" value={clusters} onChange={e => setClusters(+e.target.value)} sx={{ width: 70 }} />
                  <Typography variant="body2">Train px:</Typography>
                  <TextField size="small" type="number" value={trainPixels} onChange={e => setTrainPixels(+e.target.value)} sx={{ width: 90 }} />
                  <FormControlLabel label={<Typography variant="caption">Smooth</Typography>}
                    control={<Checkbox size="small" checked={useSmoothing} onChange={e => setUseSmoothing(e.target.checked)} />} sx={{ m: 0 }} />
                  {useSmoothing && (
                    <TextField size="small" label="Radius (px)" type="number" value={smoothRadius}
                      onChange={e => setSmoothRadius(+e.target.value)} sx={{ width: 100 }} />
                  )}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField size="small" label="Name (optional)" value={strataTitle}
                    onChange={e => setStrataTitle(e.target.value)} sx={{ flex: 1, minWidth: 150 }} />
                  <Button variant="contained" onClick={handleRunClustering}
                    disabled={running || selectedRasters.length === 0}>
                    {running ? 'Running…' : 'Run Analysis'}
                  </Button>
                </Stack>

                {strataArtifacts.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                      Strata Runs ({strataArtifacts.length}) — visible in Map View
                    </Typography>
                    {strataArtifacts.map(a => {
                      const p = typeof a.params_json === 'string' ? JSON.parse(a.params_json) : (a.params_json || {});
                      return (
                        <Typography key={a.id} variant="caption" sx={{ display: 'block' }}>
                          {a.title || `${p.nClusters || '?'} clusters`} — {fmtDate(a.created_at)}
                        </Typography>
                      );
                    })}
                  </Box>
                )}
              </Stack>
            )}

            {/* ---- SAMPLES ---- */}
            {activeStep === 'samples' && (
              <Stack spacing={2}>
                <Typography variant="h6">Generate Sample Points</Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2">Strata run:</Typography>
                  <Select size="small" value={selectedStrata} onChange={e => setSelectedStrata(e.target.value)}
                    displayEmpty sx={{ minWidth: 260 }}>
                    <MenuItem value="" disabled>Select strata run…</MenuItem>
                    {strataArtifacts.map(a => {
                      const p = typeof a.params_json === 'string' ? JSON.parse(a.params_json) : (a.params_json || {});
                      return <MenuItem key={a.id} value={String(a.id)}>{a.title || `${p.nClusters || '?'} clusters`} — {fmtDate(a.created_at)}</MenuItem>;
                    })}
                  </Select>
                  <Typography variant="body2">Per stratum:</Typography>
                  <TextField size="small" type="number" value={pointsPerStratum} onChange={e => setPointsPerStratum(+e.target.value)} sx={{ width: 60 }} />
                  <Typography variant="body2">Min dist (m):</Typography>
                  <TextField size="small" type="number" value={minDistance} onChange={e => setMinDistance(+e.target.value)} sx={{ width: 70 }} />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField size="small" label="Name (optional)" value={samplingTitle}
                    onChange={e => setSamplingTitle(e.target.value)} sx={{ flex: 1, minWidth: 150 }} />
                  <Button variant="contained" onClick={handleGeneratePoints}
                    disabled={running || !selectedStrata}>
                    {running ? 'Generating…' : 'Generate Points'}
                  </Button>
                </Stack>
                {strataArtifacts.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No strata runs yet. Complete the Strata step first.
                  </Typography>
                )}
                {artifacts.filter(a => a.tool === 'sampling-points').length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                      Sampling Runs — visible in Map View
                    </Typography>
                    {artifacts.filter(a => a.tool === 'sampling-points').map(a => (
                      <Typography key={a.id} variant="caption" sx={{ display: 'block' }}>
                        {a.title || 'Points'} — {fmtDate(a.created_at)}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* Step-aware map preview */}
        <Box sx={{ mt: 1.5 }}>
          {/* Layers step: GEE iframe for interactive layer exploration */}
          {activeStep === 'layers' && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Layer Preview — explore satellite layers before exporting
              </Typography>
              <Box
                component="iframe"
                id="gee-layers-iframe"
                ref={geeRef}
                src="https://terrra-iot.projects.earthengine.app/view/time-explorer"
                sx={{
                  width: '100%', height: 400, border: '1px solid #e0e0e0',
                  borderRadius: 1, display: 'block',
                }}
                allow="geolocation"
              />
            </>
          )}

          {/* Strata step: Leaflet map with boundary + selected strata */}
          {activeStep === 'strata' && (roiGeometry || mapStrata) && (
            <>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Preview — boundary & strata
                </Typography>
                {strataArtifacts.length > 1 && (
                  <Select size="small" value={previewStrataId || ''} displayEmpty
                    onChange={e => setPreviewStrataId(e.target.value || null)}
                    sx={{ fontSize: 11, height: 24, minWidth: 200 }}>
                    <MenuItem value="">Latest strata</MenuItem>
                    {strataArtifacts.map(a => {
                      const p = typeof a.params_json === 'string' ? JSON.parse(a.params_json) : (a.params_json || {});
                      return <MenuItem key={a.id} value={a.id}>{a.title || `${p.nClusters || '?'} clusters`} — {fmtDate(a.created_at)}</MenuItem>;
                    })}
                  </Select>
                )}
              </Stack>
              <MiniMapPreview boundary={roiGeometry} strata={mapStrata} height={340} />
            </>
          )}

          {/* Samples step: Leaflet map with strata + sample points */}
          {activeStep === 'samples' && (roiGeometry || mapStrata || mapPoints.length > 0) && (
            <>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Preview — strata & sample points
                </Typography>
                {pointsArtifacts.length > 1 && (
                  <Select size="small" value={previewPointsId || ''} displayEmpty
                    onChange={e => setPreviewPointsId(e.target.value || null)}
                    sx={{ fontSize: 11, height: 24, minWidth: 200 }}>
                    <MenuItem value="">Latest points</MenuItem>
                    {pointsArtifacts.map(a => {
                      const p = typeof a.params_json === 'string' ? JSON.parse(a.params_json) : (a.params_json || {});
                      return <MenuItem key={a.id} value={a.id}>{a.title || `${p.numPoints || '?'} points`} — {fmtDate(a.created_at)}</MenuItem>;
                    })}
                  </Select>
                )}
              </Stack>
              <MiniMapPreview boundary={roiGeometry} strata={mapStrata} points={mapPoints} height={340} />
            </>
          )}
        </Box>

        {/* Auto Wizard dialog */}
        <Dialog open={wizardOpen} onClose={() => !wizardStep && setWizardOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Pipeline Wizard</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              {/* Step 1 — Export Raster */}
              <Box sx={{ p: 1.5, border: '1px solid', borderColor: wizardResults.layers ? 'success.main' : wizardStep === 'layers' ? 'primary.main' : 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2">{wizardResults.layers ? '✓' : wizardStep === 'layers' ? '⏳' : '1.'} Export Raster</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0, my: 1 }}>
                  {LAYER_OPTIONS.map(l => (
                    <FormControlLabel key={l.key} sx={{ width: '50%', m: 0 }}
                      label={<Typography variant="caption">{l.label}</Typography>}
                      control={<Checkbox size="small" checked={wizardParams.layers.includes(l.key)} disabled={!!wizardStep}
                        onChange={e => setWizardParams(p => ({ ...p, layers: e.target.checked ? [...p.layers, l.key] : p.layers.filter(x => x !== l.key) }))} />} />
                  ))}
                </Box>
                <Stack direction="row" spacing={1}>
                  <TextField size="small" label="Start" value={wizardParams.dateStart} disabled={!!wizardStep}
                    onChange={e => setWizardParams(p => ({ ...p, dateStart: e.target.value }))} sx={{ flex: 1 }} />
                  <TextField size="small" label="End" value={wizardParams.dateEnd} disabled={!!wizardStep}
                    onChange={e => setWizardParams(p => ({ ...p, dateEnd: e.target.value }))} sx={{ flex: 1 }} />
                </Stack>
                {wizardStep === 'layers' && <LinearProgress sx={{ mt: 1 }} />}
                {wizardStep === 'layers' && <Alert severity="info" sx={{ mt: 1, py: 0 }}>Exporting to GEE Asset…</Alert>}
                {wizardStep === 'layers-done' && <Alert severity="success" sx={{ mt: 1, py: 0 }}>Raster exported.</Alert>}
                {wizardStep === 'layers-error' && <Alert severity="error" sx={{ mt: 1, py: 0 }}>{wizardResults.error}</Alert>}
                {!wizardStep && !wizardResults.layers && (
                  <Button size="small" variant="contained" sx={{ mt: 1 }} disabled={wizardParams.layers.length === 0 || !selectedProject}
                    onClick={() => runWizardStep('layers')}>Run Step 1</Button>
                )}
              </Box>

              {/* Step 2 — Clustering */}
              <Box sx={{ p: 1.5, border: '1px solid', borderColor: wizardResults.strata ? 'success.main' : wizardStep === 'strata' ? 'primary.main' : 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2">{wizardResults.strata ? '✓' : wizardStep === 'strata' ? '⏳' : '2.'} Run Clustering</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center" flexWrap="wrap">
                  <TextField size="small" label="Clusters" type="number" value={wizardParams.clusters} disabled={!!wizardStep}
                    onChange={e => setWizardParams(p => ({ ...p, clusters: +e.target.value }))} sx={{ width: 90 }} />
                  <TextField size="small" label="Train px" type="number" value={wizardParams.train} disabled={!!wizardStep}
                    onChange={e => setWizardParams(p => ({ ...p, train: +e.target.value }))} sx={{ width: 100 }} />
                  <FormControlLabel label={<Typography variant="caption">Smooth</Typography>}
                    control={<Checkbox size="small" checked={!!wizardParams.useSmoothing} disabled={!!wizardStep}
                      onChange={e => setWizardParams(p => ({ ...p, useSmoothing: e.target.checked }))} />} sx={{ m: 0 }} />
                  {wizardParams.useSmoothing && (
                    <TextField size="small" label="Radius (px)" type="number" value={wizardParams.smoothRadius} disabled={!!wizardStep}
                      onChange={e => setWizardParams(p => ({ ...p, smoothRadius: +e.target.value }))} sx={{ width: 100 }} />
                  )}
                </Stack>
                {wizardStep === 'strata' && <LinearProgress sx={{ mt: 1 }} />}
                {wizardStep === 'strata-done' && <Alert severity="success" sx={{ mt: 1, py: 0 }}>Strata saved.</Alert>}
                {wizardStep === 'strata-error' && <Alert severity="error" sx={{ mt: 1, py: 0 }}>{wizardResults.error}</Alert>}
                {wizardStep === 'layers-done' && !wizardResults.strata && (
                  <Button size="small" variant="contained" sx={{ mt: 1 }} onClick={() => runWizardStep('strata')}>Run Step 2</Button>
                )}
              </Box>

              {/* Step 3 — Sampling */}
              <Box sx={{ p: 1.5, border: '1px solid', borderColor: wizardResults.sampling ? 'success.main' : wizardStep === 'sampling' ? 'primary.main' : 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2">{wizardResults.sampling ? '✓' : wizardStep === 'sampling' ? '⏳' : '3.'} Generate Points</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <TextField size="small" label="Per stratum" type="number" value={wizardParams.points} disabled={!!wizardStep}
                    onChange={e => setWizardParams(p => ({ ...p, points: +e.target.value }))} sx={{ width: 100 }} />
                  <TextField size="small" label="Min dist (m)" type="number" value={wizardParams.distance} disabled={!!wizardStep}
                    onChange={e => setWizardParams(p => ({ ...p, distance: +e.target.value }))} sx={{ width: 110 }} />
                </Stack>
                {wizardStep === 'sampling' && <LinearProgress sx={{ mt: 1 }} />}
                {wizardResults.sampling && <Alert severity="success" sx={{ mt: 1, py: 0 }}>Points saved.</Alert>}
                {wizardStep === 'sampling-error' && <Alert severity="error" sx={{ mt: 1, py: 0 }}>{wizardResults.error}</Alert>}
                {wizardStep === 'strata-done' && !wizardResults.sampling && (
                  <Button size="small" variant="contained" sx={{ mt: 1 }} onClick={() => runWizardStep('sampling')}>Run Step 3</Button>
                )}
              </Box>

              {wizardStep === 'done' && <Alert severity="success">Pipeline complete! Results visible in Map View.</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            {wizardStep === 'done' ? (
              <Button onClick={() => { setWizardOpen(false); setWizardStep(null); setWizardResults({}); }}>Close</Button>
            ) : (
              <>
                <Button onClick={() => { setWizardOpen(false); setWizardStep(null); setWizardResults({}); }}>
                  {wizardStep ? 'Close' : 'Cancel'}
                </Button>
                {!wizardStep && (
                  <Button variant="contained" onClick={runAllWizardSteps}
                    disabled={wizardParams.layers.length === 0 || !selectedProject || !effectiveRoi}>
                    Run All Steps
                  </Button>
                )}
              </>
            )}
          </DialogActions>
        </Dialog>

      </Container>
    </>
  );
}
